const STORAGE_VISITOR = 'invo_usage_vid';
const STORAGE_SESSION = 'invo_usage_sid';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const isDev = process.env.NODE_ENV === 'development';

function randomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `v-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function getVisitorId() {
  try {
    let v = localStorage.getItem(STORAGE_VISITOR);
    if (!v) {
      v = randomId();
      localStorage.setItem(STORAGE_VISITOR, v);
    }
    return v;
  } catch {
    return randomId();
  }
}

export function getSessionId() {
  try {
    let s = sessionStorage.getItem(STORAGE_SESSION);
    if (!s) {
      s = randomId();
      sessionStorage.setItem(STORAGE_SESSION, s);
    }
    return s;
  } catch {
    return randomId();
  }
}

const queue = [];
let flushTimer = null;

function buildIngestBody(eventsSlice) {
  return {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    events: eventsSlice.map((e) => ({
      type: e.type,
      path: e.path,
      duration_seconds: e.duration_seconds,
      meta: e.meta,
    })),
  };
}

async function postIngest(body) {
  const res = await fetch(`${API_BASE}/api/analytics/ingest`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });
  return res;
}

export function enqueueUsageEvent(type, payload = {}) {
  queue.push({
    type,
    path: payload.path ?? (typeof window !== 'undefined' ? window.location.pathname : '/'),
    duration_seconds: payload.duration_seconds,
    meta: payload.meta,
  });
  scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushAnalyticsQueue();
  }, 400);
}

/**
 * Sends queued events to the API. Surfaces failures in development (CORS / 422 / 429).
 * Retries once on network error, 429, or 5xx (same payload; no duplicate rows if retry succeeds).
 */
export async function flushAnalyticsQueue() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, 50);
  const body = buildIngestBody(batch);

  const logFailure = async (res) => {
    const text = await res.text().catch(() => '');
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn(
        '[analytics] ingest failed',
        res.status,
        res.statusText,
        text.slice(0, 200),
        '→ Production: set Koyeb CORS_ALLOWED_ORIGINS to this page origin (exact URL, no trailing slash). Koyeb metrics count all HTTP traffic; the admin dashboard only counts usage_analytics_events rows.'
      );
    }
  };

  const tryOnce = async () => {
    try {
      const res = await postIngest(body);
      if (res.ok) {
        return { ok: true };
      }
      await logFailure(res);
      const retryable = res.status === 429 || res.status >= 500;
      return { ok: false, retryable };
    } catch (e) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.warn('[analytics] ingest network error (often CORS or wrong API URL)', e?.message || e);
      }
      return { ok: false, retryable: true };
    }
  };

  const first = await tryOnce();
  if (first.ok) {
    return;
  }
  if (first.retryable) {
    const second = await tryOnce();
    if (second.ok) {
      return;
    }
  }
  queue.unshift(...batch);
}

/**
 * Best-effort delivery when the page is hidden/closed (avoids losing the debounced batch).
 */
function flushWithSendBeacon() {
  if (queue.length === 0 || typeof navigator === 'undefined' || !navigator.sendBeacon) {
    return;
  }
  const batch = queue.splice(0, 50);
  const body = JSON.stringify(buildIngestBody(batch));
  const url = `${API_BASE}/api/analytics/ingest`;
  const blob = new Blob([body], { type: 'application/json' });
  try {
    navigator.sendBeacon(url, blob);
  } catch {
    queue.unshift(...batch);
  }
  if (queue.length > 0) {
    flushWithSendBeacon();
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener(
    'pagehide',
    () => {
      if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
      flushWithSendBeacon();
    },
    { capture: false }
  );
}

export function startUsageHeartbeat(intervalMs = 25000) {
  const seconds = Math.max(1, Math.round(intervalMs / 1000));
  const tick = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      enqueueUsageEvent('heartbeat', {
        duration_seconds: seconds,
        path: window.location.pathname,
      });
    }
  };
  const id = setInterval(tick, intervalMs);
  return () => clearInterval(id);
}

export function getAnalyticsContext() {
  return {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
  };
}
